import React from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import type { PortalProviderAPI } from '@atlaskit/editor-common/common';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { EditorState, ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import { SnippetLoadingPlaceholder } from '../ui/SnippetLoadingPlaceholder';

export const snippetsPluginKey: PluginKey<SnippetsPluginState> = new PluginKey<SnippetsPluginState>(
	'snippetsPlugin',
);

/** Meta action: add a transient loader decoration at a doc position. */
export const SNIPPETS_META_ADD_LOADER = 'ADD_LOADER' as const;

/** Meta action: remove a transient loader decoration by id. */
export const SNIPPETS_META_REMOVE_LOADER = 'REMOVE_LOADER' as const;

/** Meta action: record a terminally removed snippet id for the editor instance. */
export const SNIPPETS_META_REMOVE_SNIPPET = 'REMOVE_SNIPPET' as const;

/** Prefix for portal keys and widget keys tied to the snippet loader UI. */
export const SNIPPET_LOADER_PORTAL_PREFIX = 'snippet-loader-';

export interface PendingSnippetLoader {
	id: string;
	pos: number;
}

export interface SnippetsPluginState {
	decorationSet: DecorationSet;
	pending: PendingSnippetLoader[];
	removedSnippetIds: ReadonlySet<string>;
}

export type SnippetsMeta =
	| { loader: PendingSnippetLoader; type: typeof SNIPPETS_META_ADD_LOADER }
	| { id: string; type: typeof SNIPPETS_META_REMOVE_LOADER }
	| { snippetId: string; type: typeof SNIPPETS_META_REMOVE_SNIPPET };

function createLoaderDOM(
	portalProviderAPI: PortalProviderAPI,
	loader: PendingSnippetLoader,
): HTMLElement {
	const dom = getDocument()?.createElement('div');
	if (!dom) {
		throw new Error('Document unavailable for snippet loader');
	}
	dom.setAttribute('data-snippet-loader', loader.id);
	const portalKey = `${SNIPPET_LOADER_PORTAL_PREFIX}${loader.id}`;
	portalProviderAPI.render(() => React.createElement(SnippetLoadingPlaceholder), dom, portalKey);

	return dom;
}

export const createPlugin = (
	portalProviderAPI: PortalProviderAPI,
): SafePlugin<SnippetsPluginState> =>
	new SafePlugin<SnippetsPluginState>({
		key: snippetsPluginKey,

		state: {
			init(): SnippetsPluginState {
				return {
					pending: [],
					decorationSet: DecorationSet.empty,
					removedSnippetIds: new Set(),
				};
			},

			apply(
				tr: ReadonlyTransaction,
				prevState: SnippetsPluginState,
				_oldEditorState: EditorState,
				newEditorState: EditorState,
			): SnippetsPluginState {
				const meta: SnippetsMeta | undefined = tr.getMeta(snippetsPluginKey);

				if (prevState.pending.length === 0 && !meta) {
					return prevState;
				}

				if (!meta) {
					const mappedPending = prevState.pending.map((p: PendingSnippetLoader) => ({
						...p,
						pos: tr.mapping.map(p.pos),
					}));
					return {
						pending: mappedPending,
						decorationSet: prevState.decorationSet.map(tr.mapping, newEditorState.doc),
						removedSnippetIds: prevState.removedSnippetIds,
					};
				}

				let pending = prevState.pending.map((p: PendingSnippetLoader) => ({
					...p,
					pos: tr.mapping.map(p.pos),
				}));

				if (meta.type === SNIPPETS_META_ADD_LOADER) {
					pending = [...pending, meta.loader];
				}
				if (meta.type === SNIPPETS_META_REMOVE_LOADER) {
					pending = pending.filter((p: PendingSnippetLoader) => p.id !== meta.id);
				}
				const removedSnippetIds =
					meta.type === SNIPPETS_META_REMOVE_SNIPPET &&
					!prevState.removedSnippetIds.has(meta.snippetId)
						? new Set([...prevState.removedSnippetIds, meta.snippetId])
						: prevState.removedSnippetIds;
				const decorations = pending.map((loader: PendingSnippetLoader) =>
					Decoration.widget(loader.pos, () => createLoaderDOM(portalProviderAPI, loader), {
						key: `${SNIPPET_LOADER_PORTAL_PREFIX}${loader.id}`,
						side: -1,
						destroy: () => {
							portalProviderAPI.remove(`${SNIPPET_LOADER_PORTAL_PREFIX}${loader.id}`);
						},
					}),
				);

				return {
					pending,
					decorationSet: DecorationSet.create(newEditorState.doc, decorations),
					removedSnippetIds,
				};
			},
		},

		props: {
			decorations(state: EditorState) {
				return snippetsPluginKey.getState(state)?.decorationSet ?? DecorationSet.empty;
			},
		},
	});
